import apiService from "../api/apiService";

/**
 * True when the dashboard runs as the installed app (opened from the phone's
 * home screen) and not in a browser tab. The installed app shows only the
 * orders-and-shipping screen.
 */
export function isInstalledApp(): boolean {
	try {
		// The manifest's start_url carries ?source=pwa; remember it for the session
		// because the query string is gone after the first navigation.
		if (new URLSearchParams(window.location.search).get("source") === "pwa") {
			sessionStorage.setItem("styola-installed-app", "1");
		}
		return (
			window.matchMedia("(display-mode: standalone)").matches ||
			(navigator as any).standalone === true ||
			sessionStorage.getItem("styola-installed-app") === "1"
		);
	} catch {
		return false;
	}
}

const PENDING_CACHE = "styola-notification";
const PENDING_KEY = "/__pending-notification";

const orderIn = (url: string): string | null => {
	try {
		return new URL(url, window.location.origin).searchParams.get("order");
	} catch {
		return null;
	}
};

/**
 * Calls back with the order a tapped notification points at.
 *  - App already open: the service worker sends it as a message.
 *  - App opened by the tap: the service worker left it in a small cache, because
 *    iPhone starts the installed app at its start screen whatever address was asked for.
 * Returns a function that stops listening.
 */
export function onNotificationOpen(callback: (orderId: string) => void): () => void {
	if (!("serviceWorker" in navigator)) return () => undefined;

	const onMessage = (event: MessageEvent) => {
		if (event.data?.type !== "styola-open") return;
		const orderId = orderIn(String(event.data.url || ""));
		if (orderId) callback(orderId);
	};
	navigator.serviceWorker.addEventListener("message", onMessage);

	if ("caches" in window) {
		caches
			.open(PENDING_CACHE)
			.then(async (cache) => {
				const response = await cache.match(PENDING_KEY);
				if (!response) return;
				await cache.delete(PENDING_KEY);
				const pending = await response.json();
				// Only a tap from the last couple of minutes; an old one must not hijack a later visit.
				if (Date.now() - Number(pending?.at || 0) > 2 * 60 * 1000) return;
				const orderId = orderIn(String(pending?.url || ""));
				if (orderId) callback(orderId);
			})
			.catch(() => undefined);
	}

	return () => navigator.serviceWorker.removeEventListener("message", onMessage);
}

/** Registers the service worker that makes the app installable and shows notifications. */
export function registerServiceWorker(): void {
	if (!("serviceWorker" in navigator)) return;
	window.addEventListener("load", () => {
		navigator.serviceWorker.register("/sw.js").catch((error) => {
			console.error("Service worker registration failed", error);
		});
	});
}

// Tells the shared API client not to raise its own error pop-up for these calls.
const SILENT: any = { silentError: true };

export type PushState = "unsupported" | "blocked" | "on" | "off";

const pushSupported = () =>
	"serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

/** Whether this device currently receives shipment notifications. */
export async function getPushState(): Promise<PushState> {
	if (!pushSupported()) return "unsupported";
	if (Notification.permission === "denied") return "blocked";
	try {
		const registration = await navigator.serviceWorker.ready;
		return (await registration.pushManager.getSubscription()) ? "on" : "off";
	} catch {
		return "off";
	}
}

function toKeyBytes(base64Url: string): Uint8Array {
	const padded = (base64Url + "=".repeat((4 - (base64Url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
	const raw = atob(padded);
	return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/**
 * Asks for permission and registers this device for shipment notifications.
 * Returns a message to show the merchant.
 */
export function enablePush(): Promise<{ ok: boolean; message: string }> {
	// One at a time: a second tap while the first is still registering made the
	// browser hand out several registrations for the same phone.
	if (!enabling) {
		enabling = doEnablePush().finally(() => {
			enabling = null;
		});
	}
	return enabling;
}

let enabling: Promise<{ ok: boolean; message: string }> | null = null;

async function doEnablePush(): Promise<{ ok: boolean; message: string }> {
	if (!pushSupported()) {
		return {
			ok: false,
			message: "Notifications are not supported here. On iPhone, add this app to your Home Screen first (Share → Add to Home Screen).",
		};
	}
	const permission = await Notification.requestPermission();
	if (permission !== "granted") {
		return { ok: false, message: "Notifications are blocked. Allow them for this app in your phone's settings." };
	}
	try {
		const keyResponse: any = await apiService.get("/notifications/webpush/public-key");
		const key = keyResponse?.data?.data?.publicKey;
		if (!keyResponse?.data?.data?.enabled || !key) {
			return { ok: false, message: "Notifications are not available right now. Please try again later." };
		}
		const registration = await navigator.serviceWorker.ready;
		const subscription =
			(await registration.pushManager.getSubscription()) ||
			(await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKeyBytes(key) }));
		await apiService.post("/notifications/webpush/subscribe", subscription.toJSON(), SILENT);
		return { ok: true, message: "Notifications are on for this device." };
	} catch (error) {
		console.error("Could not turn on notifications", error);
		return { ok: false, message: "Could not turn on notifications. Please try again." };
	}
}

/**
 * Tells the server that this device's alerts belong to the merchant signed in now.
 * A subscription lives in the browser, not in the login, so without this a second
 * merchant signing in on the same phone would keep receiving the first one's alerts.
 */
export function refreshPushOwner(): Promise<void> {
	// One at a time: two of these together would register the same device twice.
	if (!refreshing) {
		refreshing = doRefreshPushOwner().finally(() => {
			refreshing = null;
		});
	}
	return refreshing;
}

let refreshing: Promise<void> | null = null;

async function doRefreshPushOwner(): Promise<void> {
	if (!pushSupported()) return;
	try {
		const registration = await navigator.serviceWorker.ready;
		const subscription = await registration.pushManager.getSubscription();
		if (subscription) await apiService.post("/notifications/webpush/subscribe", subscription.toJSON(), SILENT);
	} catch (error) {
		console.error("Could not refresh the notification registration", error);
	}
}

/** Stops shipment notifications on this device. */
export async function disablePush(): Promise<void> {
	if (!pushSupported()) return;
	try {
		const registration = await navigator.serviceWorker.ready;
		const subscription = await registration.pushManager.getSubscription();
		if (!subscription) return;
		await apiService.post("/notifications/webpush/unsubscribe", { endpoint: subscription.endpoint }, SILENT).catch(() => undefined);
		await subscription.unsubscribe();
	} catch (error) {
		console.error("Could not turn off notifications", error);
	}
}
